import { useState } from 'react';
import { formatCurrency } from '../../lib/format';
import { X, Minus, AlertTriangle } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';

interface InvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  productId?: string;
}

interface InvoiceForCredit {
  _id: string;
  invoiceNumber: string;
  status: string;
  items: InvoiceItem[];
  totalAmount: number;
  shippingAmount: number;
  amountPaid: number;
  creditApplied?: number;
}

interface Props {
  invoice: InvoiceForCredit;
  onClose: () => void;
  onSuccess: () => void;
}

export function IssueCreditModal({ invoice, onClose, onSuccess }: Props) {
  const [reason, setReason] = useState('');
  const [selectedItems, setSelectedItems] = useState<Map<number, { quantity: number; unitPrice: number }>>(new Map());
  const [creditShipping, setCreditShipping] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [creditAll, setCreditAll] = useState(false);

  const toggleItem = (index: number) => {
    const next = new Map(selectedItems);
    if (next.has(index)) {
      next.delete(index);
    } else {
      const item = invoice.items[index];
      next.set(index, { quantity: item.quantity, unitPrice: item.unitPrice });
    }
    setSelectedItems(next);
  };

  const updateItemCredit = (index: number, field: 'quantity' | 'unitPrice', value: number) => {
    const next = new Map(selectedItems);
    const current = next.get(index);
    if (current) {
      next.set(index, { ...current, [field]: value });
      setSelectedItems(next);
    }
  };

  const handleCreditAll = () => {
    if (creditAll) {
      setSelectedItems(new Map());
      setCreditShipping(false);
      setCreditAll(false);
    } else {
      const all = new Map<number, { quantity: number; unitPrice: number }>();
      invoice.items.forEach((item, i) => {
        all.set(i, { quantity: item.quantity, unitPrice: item.unitPrice });
      });
      setSelectedItems(all);
      setCreditShipping(true);
      setCreditAll(true);
    }
  };

  const totalItemCredit = Array.from(selectedItems.entries()).reduce((sum, [, credit]) => {
    return sum + credit.quantity * credit.unitPrice;
  }, 0);

  const shippingCredit = creditShipping ? invoice.shippingAmount : 0;
  const totalCredit = totalItemCredit + shippingCredit;
  const existingCredits = invoice.creditApplied || 0;
  const maxCredit = invoice.totalAmount - existingCredits;
  const exceedsMax = totalCredit > maxCredit;

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    if (selectedItems.size === 0 && !creditShipping) {
      toast.error('Select at least one item or shipping to credit');
      return;
    }
    if (exceedsMax) {
      toast.error('Credit amount exceeds invoice total');
      return;
    }

    const items = Array.from(selectedItems.entries()).map(([idx, credit]) => {
      const original = invoice.items[idx];
      return {
        description: original.description,
        quantity: credit.quantity,
        unitPrice: credit.unitPrice,
        totalPrice: credit.quantity * credit.unitPrice,
        productId: original.productId
      };
    });

    try {
      setSubmitting(true);
      const response = await apiClient.post('/credit-memos', {
        originalInvoiceId: invoice._id,
        items,
        shippingCredit: creditShipping ? invoice.shippingAmount : 0,
        reason: reason.trim()
      });

      if (response.success) {
        toast.success(`Credit memo ${response.data.creditMemoNumber} issued`);
        onSuccess();
        onClose();
      } else {
        toast.error(response.error || 'Failed to issue credit memo');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to issue credit memo');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]" onClick={onClose}>
      <div className="relative top-10 mx-auto p-0 w-full max-w-lg shadow-lg rounded-lg bg-white" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Issue Credit Memo</h3>
            <p className="text-sm text-gray-500">Against {invoice.invoiceNumber}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4 max-h-[65vh] overflow-y-auto">
          {/* Credit All toggle */}
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <input
              type="checkbox"
              checked={creditAll}
              onChange={handleCreditAll}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Credit full invoice ({formatCurrency(invoice.totalAmount)})
          </label>

          {/* Line items */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Line Items</p>
            {invoice.items.map((item, idx) => {
              const isSelected = selectedItems.has(idx);
              const credit = selectedItems.get(idx);
              return (
                <div key={idx} className={`border rounded-lg p-3 transition-colors ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleItem(idx)}
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.description}</p>
                      <p className="text-xs text-gray-500">
                        {item.quantity} x {formatCurrency(item.unitPrice)} = {formatCurrency(item.totalPrice)}
                      </p>
                    </div>
                  </label>
                  {isSelected && credit && (
                    <div className="mt-2 ml-6 flex gap-2">
                      <div>
                        <label className="text-[10px] text-gray-500 uppercase">Qty to credit</label>
                        <input
                          type="number"
                          defaultValue={credit.quantity}
                          key={`qty-${idx}-${item.quantity}`}
                          min={1}
                          max={item.quantity}
                          onChange={e => {
                            const val = parseInt(e.target.value);
                            if (!isNaN(val)) updateItemCredit(idx, 'quantity', val);
                          }}
                          onBlur={e => {
                            const val = parseInt(e.target.value);
                            const clamped = Math.max(1, Math.min(isNaN(val) ? 1 : val, item.quantity));
                            updateItemCredit(idx, 'quantity', clamped);
                            e.target.value = String(clamped);
                          }}
                          className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500 uppercase">Unit price</label>
                        <input
                          type="number"
                          defaultValue={credit.unitPrice}
                          key={`price-${idx}-${item.unitPrice}`}
                          step="0.01"
                          min={0}
                          max={item.unitPrice}
                          onChange={e => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val)) updateItemCredit(idx, 'unitPrice', val);
                          }}
                          onBlur={e => {
                            const val = parseFloat(e.target.value);
                            const clamped = Math.max(0, Math.min(isNaN(val) ? 0 : val, item.unitPrice));
                            updateItemCredit(idx, 'unitPrice', clamped);
                            e.target.value = clamped.toFixed(2);
                          }}
                          className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </div>
                      <div className="flex items-end pb-1">
                        <span className="text-sm font-mono text-gray-700">= {formatCurrency(credit.quantity * credit.unitPrice)}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Shipping credit */}
          {invoice.shippingAmount > 0 && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={creditShipping}
                onChange={e => setCreditShipping(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Credit shipping ({formatCurrency(invoice.shippingAmount)})
            </label>
          )}

          {/* Reason */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={2}
              placeholder="e.g. Price correction, lost shipment, damaged goods..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Items credit:</span>
              <span className="font-mono">{formatCurrency(totalItemCredit)}</span>
            </div>
            {shippingCredit > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Shipping credit:</span>
                <span className="font-mono">{formatCurrency(shippingCredit)}</span>
              </div>
            )}
            {existingCredits > 0 && (
              <div className="flex justify-between text-sm text-amber-600">
                <span>Existing credits:</span>
                <span className="font-mono">{formatCurrency(existingCredits)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm font-bold border-t pt-1">
              <span>Total credit:</span>
              <span className={`font-mono ${exceedsMax ? 'text-red-600' : 'text-green-600'}`}>
                {formatCurrency(totalCredit)}
              </span>
            </div>
            {invoice.status === 'paid' && totalCredit > 0 && (
              <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                <AlertTriangle className="h-3 w-3" />
                Invoice is paid — credit will be added as physician balance
              </p>
            )}
          </div>

          {exceedsMax && (
            <p className="text-xs text-red-600 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Credit ({formatCurrency(totalCredit)}) exceeds remaining invoice amount ({formatCurrency(maxCredit)})
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || exceedsMax || totalCredit === 0 || !reason.trim()}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
          >
            <Minus className="h-3 w-3" />
            {submitting ? 'Issuing...' : `Issue Credit ${formatCurrency(totalCredit)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
